<script setup>
  import {computed, inject, onMounted, onUnmounted, ref, watch} from "vue";

  import ComponentAiOptInDialog from "@components/Dialogs/AiOptInDialog.vue";
  import CryptDialogsDefault from "@components/Layout/Components/CryptDialogs/Default.vue";
  import CryptDialogsEducation from "@components/Layout/Components/CryptDialogs/Education.vue";
  import CryptDialogsError from "@components/Layout/Components/CryptDialogs/Error.vue";
  import CryptDialogsOnboarding from "@components/Layout/Components/CryptDialogs/Onboarding.vue";
  import CryptDialogsRecovery from "@components/Layout/Components/CryptDialogs/Recovery.vue";
  import {router, usePage} from "@inertiajs/vue3";
  import Auth from "@utils/Auth.js";

  defineOptions({
    name: "LayoutDialogCrypt",
  });

  const page = usePage();

  const privacy = inject("$privacy");

  const refCryptDialog = ref(null);
  const refAiOptInDialog = ref(null);

  const showDialog = ref(false);
  const posIntegration = ref({
    mergeAccountUid: null,
    validateMergeAccountUid: null,
    baseUrl: import.meta.env.MC_API_DATA_BASE_URL + "/merge-account/",
  });

  const isLoading = computed(() => privacy.getIsLoading());

  watch(
    () => refCryptDialog.value,
    (newValue, oldValue) => {
      if (privacy.hasOneOfCryptStatus(["VERIFICATION_VALID", "RECOVERY_VALID"])) {
        showDialog.value = false;
        if (oldValue) oldValue.close();

        router.reload({only: ["client", "isEditable"]});
      } else {
        if (newValue) newValue.open();
      }
    },
  );

  watch(
    () => privacy.getCurrentStatus(),
    () => {
      if (privacy.hasOneOfCryptStatus(["VERIFICATION_VALID", "RECOVERY_VALID"])) {
        router.reload({only: ["client", "isEditable"]});
        closeDialog();
      }
    },
  );

  onMounted(() => {
    globalThis.addEventListener("focus", checkCrypt);
    globalThis.addEventListener("visibilitychange", checkCryptWhenVisible);

    setInterval(() => {
      checkCrypt();
    }, 30_000);
  });

  router.on("navigate", () => {
    checkCrypt();
  });

  onUnmounted(() => {
    if (showDialog.value) {
      closeDialog();
    }

    globalThis.removeEventListener("focus", checkCrypt);
    globalThis.removeEventListener("visibilitychange", checkCryptWhenVisible);
  });

  const checkCrypt = () => {
    // Redirect if your recovery code is correct and you want to set a new privacy password.
    if (privacy.hasCryptStatus("RECOVERY_VALID")) {
      if (!route().current("privacyPassword.show")) {
        router.get(route("privacyPassword.show", {user: page.props.client?.users[0].id}));
      }
      return;
    }

    if (!showDialog.value) {
      privacy.whenCryptReady(() => {
        closeDialog();
      });

      privacy.checkPrivacyVaultPasswordValidity(
        () => closeDialog(),
        () => openDialog(),
      );
    }
  };

  const checkCryptWhenVisible = () => {
    if (!document.hidden) {
      checkCrypt();
    }
  };

  const logout = () => {
    Auth.logout(privacy);
  };

  const closeDialog = () => {
    if (refCryptDialog.value) refCryptDialog.value.close();
    showDialog.value = false;
  };

  const openDialog = () => {
    if (refCryptDialog.value) refCryptDialog.value.open();
    showDialog.value = true;
  };

  const handlePostCryptDialog = () => {
    // show ai opt in dialog for "pro" licenses and when
    // user has not seen the dialog, yet
    if (page.props.client.license.is_pro && privacy.getConfiguration("hideAiOptInDialog")?.amount !== true) {
      setTimeout(() => {
        refAiOptInDialog.value.open();
      }, 1000);
    }
  };
</script>

<template>
  <div
    v-if="showDialog"
    class="fixed z-40 h-screen w-full backdrop-blur-lg print:hidden"
    :data-test="getTestId('crypt-backdrop')"
  />

  <template v-if="privacy.hasCryptStatus('SERVER_ERROR')">
    <CryptDialogsError ref="refCryptDialog" :privacy="privacy" :is-loading="isLoading" :test-id="getTestId('crypt')" />
  </template>

  <template v-if="privacy.hasAccountEducationStatus()">
    <CryptDialogsEducation
      ref="refCryptDialog"
      :privacy="privacy"
      :is-loading="isLoading"
      :logout="logout"
      :test-id="getTestId('crypt')"
    />
  </template>

  <template v-else>
    <template
      v-if="
        privacy.hasOneOfCryptStatus([
          'ENCRYPTION_NOT_SET',
          'ENCRYPTION_PENDING',
          'ENCRYPTION_SET',
          'ENCRYPTION_INVALID',
        ])
      "
    >
      <CryptDialogsOnboarding
        ref="refCryptDialog"
        :privacy="privacy"
        :is-loading="isLoading"
        :pos-integration="posIntegration"
        :test-id="getTestId('crypt')"
      />
    </template>

    <template v-if="privacy.hasOneOfCryptStatus(['RECOVERY', 'RECOVERY_VERIFICATION_INVALID'])">
      <CryptDialogsRecovery
        ref="refCryptDialog"
        :privacy="privacy"
        :is-loading="isLoading"
        :logout="logout"
        :test-id="getTestId('crypt')"
      />
    </template>

    <template v-if="privacy.hasOneOfCryptStatus(['VERIFICATION_PENDING', 'VERIFICATION_INVALID'])">
      <CryptDialogsDefault
        ref="refCryptDialog"
        :privacy="privacy"
        :is-loading="isLoading"
        :logout="logout"
        :test-id="getTestId('crypt')"
        @success="handlePostCryptDialog"
      />
    </template>
  </template>

  <component-ai-opt-in-dialog ref="refAiOptInDialog" />
</template>
